import React, { useState, useMemo, useEffect, useCallback } from "react";
import { calculateEndTime, formatTime } from "../../helpers";
import { NewClassForm } from "../../types";

/**
 * Default values for a new class form. Exported for external comparison (dirty state detection).
 */
export const initialNewClassForm: NewClassForm = {
  name: "",
  startTime: "18:00",
  duration: 60,
  capacity: 15,
  instructor: "Kat",
  studio: "Roots & Wings",
  description: "",
  type: "one-off",
  daysToRepeat: 100,
  paymentType: "none",
  cost: "",
  allowArrangements: false,
};

interface AddClassFormProps {
  selectedDay: Date;
  onAddClass: (newClass: NewClassForm) => Promise<void>;
  onFormChange?: (isDirty: boolean) => void;
  onCancel?: () => void;
}

const AddClassForm: React.FC<AddClassFormProps> = ({
  selectedDay,
  onAddClass,
  onFormChange,
  onCancel,
}) => {
  const [newClass, setNewClass] = useState<NewClassForm>(initialNewClassForm);

  /**
   * Checks if the current form state differs from the initial defaults.
   */
  const checkIsDirty = useCallback((formData: NewClassForm): boolean => {
    return (
      formData.name !== initialNewClassForm.name ||
      formData.startTime !== initialNewClassForm.startTime ||
      formData.duration !== initialNewClassForm.duration ||
      formData.capacity !== initialNewClassForm.capacity ||
      formData.instructor !== initialNewClassForm.instructor ||
      formData.studio !== initialNewClassForm.studio ||
      formData.description !== initialNewClassForm.description ||
      formData.type !== initialNewClassForm.type ||
      formData.paymentType !== initialNewClassForm.paymentType ||
      formData.cost !== initialNewClassForm.cost ||
      formData.allowArrangements !== initialNewClassForm.allowArrangements
    );
  }, []);

  // Notify parent of dirty state changes
  useEffect(() => {
    if (onFormChange) {
      onFormChange(checkIsDirty(newClass));
    }
  }, [newClass, onFormChange, checkIsDirty]);

  /**
   * Resets the form to default values and notifies parent.
   */
  const handleCancel = () => {
    setNewClass(initialNewClassForm);
    if (onCancel) {
      onCancel();
    }
  };

  const durationValue =
    typeof newClass.duration === "string" ? 0 : newClass.duration;
  const calculatedEndTime = useMemo(() => {
    return calculateEndTime(newClass.startTime, durationValue);
  }, [newClass.startTime, durationValue]);

  const handleSubmit = async () => {
    await onAddClass(newClass);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-bold text-slate-400 block mb-1">
            Class Name
          </label>
          <input
            className="w-full p-2 bg-slate-800 border-slate-600 rounded text-slate-200"
            value={newClass.name}
            onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
            placeholder="Vinyasa Flow"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-400 block mb-1">
            Studio
          </label>
          <input
            className="w-full p-2 bg-slate-800 border-slate-600 rounded text-slate-200"
            value={newClass.studio}
            onChange={(e) =>
              setNewClass({ ...newClass, studio: e.target.value })
            }
            placeholder="Roots & Wings"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold text-slate-400 uppercase mb-1">
          Description (Optional)
        </label>
        <textarea
          name="description"
          className="w-full p-2 bg-slate-800 border border-slate-600 rounded text-slate-200"
          value={newClass.description || ""}
          onChange={(e) =>
            setNewClass({ ...newClass, description: e.target.value })
          }
          maxLength={2000}
          rows={3}
          placeholder="Add class description..."
        />
        <div className="text-right text-xs text-slate-500">
          {newClass.description?.length || 0}/2000
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div>
          <label className="text-xs font-bold text-slate-400 block mb-1">
            Start
          </label>
          <input
            type="time"
            className="w-full p-2 bg-slate-800 border-slate-600 rounded text-slate-200 appearance-none"
            style={{ WebkitAppearance: "none" }}
            value={newClass.startTime}
            onChange={(e) =>
              setNewClass({ ...newClass, startTime: e.target.value })
            }
          />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-400 block mb-1">
            Dur (min)
          </label>
          <input
            name="duration"
            type="number"
            className="w-full p-2 bg-slate-800 border-slate-600 rounded text-slate-200"
            value={newClass.duration}
            onChange={(e) =>
              setNewClass({
                ...newClass,
                duration: e.target.value === "" ? "" : parseInt(e.target.value),
              })
            }
          />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-400 block mb-1">
            End
          </label>
          <input
            type="text"
            className="w-full p-2 bg-slate-800 border-slate-600 rounded text-slate-500 italic cursor-not-allowed"
            readOnly
            value={formatTime(calculatedEndTime)}
          />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-400 block mb-1">
            Cap
          </label>
          <input
            name="capacity"
            type="number"
            className="w-full p-2 bg-slate-800 border-slate-600 rounded text-slate-200"
            value={newClass.capacity}
            onChange={(e) =>
              setNewClass({
                ...newClass,
                capacity: e.target.value === "" ? "" : parseInt(e.target.value),
              })
            }
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-bold text-slate-400 block mb-1">
            Payment
          </label>
          <select
            className="w-full p-2 bg-slate-800 border-slate-600 rounded text-slate-200"
            value={newClass.paymentType}
            onChange={(e) =>
              setNewClass({ ...newClass, paymentType: e.target.value as any })
            }
          >
            <option value="none">None</option>
            <option value="venmo">Venmo</option>
          </select>
        </div>
        {newClass.paymentType === "venmo" && (
          <div>
            <label className="text-xs font-bold text-slate-400 block mb-1">
              Cost ($)
            </label>
            <input
              type="number"
              placeholder="Empty = Pay What You Can"
              className="w-full p-2 bg-slate-800 border-slate-600 rounded text-slate-200"
              value={newClass.cost}
              onChange={(e) =>
                setNewClass({ ...newClass, cost: e.target.value })
              }
            />
          </div>
        )}
      </div>

      {newClass.paymentType === "venmo" && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="addAllowArrangements"
            checked={newClass.allowArrangements}
            onChange={(e) =>
              setNewClass({ ...newClass, allowArrangements: e.target.checked })
            }
            className="w-4 h-4"
          />
          <label
            htmlFor="addAllowArrangements"
            className="text-sm text-slate-400 cursor-pointer select-none"
          >
            Allow "Alternate Arrangements" (Bypass Payment)
          </label>
        </div>
      )}

      <div className="pt-4 border-t border-slate-700 flex flex-col sm:flex-row gap-4 sm:items-center">
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-slate-300">
            <input
              type="radio"
              checked={newClass.type === "one-off"}
              onChange={() => setNewClass({ ...newClass, type: "one-off" })}
            />{" "}
            One-off
          </label>
          <label className="flex items-center gap-2 text-slate-300">
            <input
              type="radio"
              checked={newClass.type === "recurring"}
              onChange={() => setNewClass({ ...newClass, type: "recurring" })}
            />{" "}
            Recurring
          </label>
        </div>
        <div className="flex gap-2 sm:ml-auto w-full sm:w-auto">
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 sm:flex-none bg-slate-700 hover:bg-slate-600 text-slate-200 px-6 py-2 rounded"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 sm:flex-none bg-fuchsia-700 hover:bg-fuchsia-600 text-white px-6 py-2 rounded"
          >
            Create Class
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddClassForm;
